import { useQuery } from "@apollo/client";
import { StoreEntityResponseCollection } from "gql-types";
import { globalStoresByCatName } from "gql/queries";
import { likesFormatter, rateFormatter } from "helpers";
import { TOP_STORES } from "helpers/constants";
import { useWindowSize } from "helpers/scroll/animateScroll";
import Image from "next/image";
import React, { useState } from "react";
import Skeleton from "react-loading-skeleton";
import TbalerMoodEmpty from "public/images/tabler_mood-empty.svg";
import { useTranslation } from "react-i18next";
import { useGlobalState } from "context/GlobalStateContext";

const TopStores = ({
  categoryId,
  categorySlug,
  itemsPerPage,
}: {
  categoryId: string;
  itemsPerPage: number;
}) => {
  const size = useWindowSize();
  const [stores, setStores] = useState([]);
  const [isLoading, setIsloading] = useState(true);
  const { t: translate } = useTranslation("categories_carousel");
  const {} = useQuery<{
    stores?: StoreEntityResponseCollection;
  }>(globalStoresByCatName, {
    variables: {
      filterOptions: {
        sub_category: { category: { id: { eq: categoryId } } },
        id: {
          in:
            TOP_STORES.find((category) => category.categoryId === categoryId)?.favoriteStores || [],
        },
      },
      page: 1,
      itemsPerPage,
    },
    onCompleted: (e) => {
      setStores(e?.stores?.data);
      setIsloading(false);
    },
  });

  const goToStore = (storeSlug: string) => {
    window.location.href = `/categories/${categorySlug}/${storeSlug}`;
  };
  const { direction } = useGlobalState();
  const reverseCols = direction === "rtl" ? "flex-row-reverse" : "";
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 w-[100%]  lg:h-36">
      {isLoading ? (
        (size.width > 768 ? [1, 2, 3, 4] : [1, 2]).map((item, index) => (
          <div
            key={index}
            className=" rounded-lg h-16 items-center justify-between hover:bg-[#D9D9D9] cursor-pointer"
          >
            <Skeleton
              key={`__index__carousel__card__${index}`}
              className="bg-[#FAFAFA] rounded-lg h-16 flex items-center justify-between hover:bg-[#D9D9D9]"
            />
          </div>
        ))
      ) : (
        <>
          {stores.length ? (
            stores.map((item, index) => (
              <div
                id="popular_category_store_link"
                className="z-10"
                onClick={($event) => {
                  $event.preventDefault();
                  $event.stopPropagation();
                  goToStore(item?.attributes?.slug);
                }}
                key={index}
              >
                <div
                  className={`bg-white rounded-lg h-16 flex items-center ${reverseCols} justify-between hover:bg-[#D9D9D9] cursor-pointer`}
                >
                  <div className={`flex items-center ${reverseCols}`}>
                    <div className="self-center w-12 h-12 fill relative ltr:mr-2 rtl:ml-2 ltr:ml-2 rtl:mr-2">
                      <Image
                        className="rounded-full border-2 border-orange-300 mx-2"
                        src={item?.attributes?.mainImage?.data?.attributes?.url}
                        alt="micro-icon"
                        width={100}
                        height={100}
                      />
                      <div className="w-4 h-4 rounded-full absolute bottom-0 right-1 bg-white p-[2.5px]">
                        <Image
                          className="rounded-full"
                          src={`/images/micro-icon-${item?.attributes?.platform?.toLowerCase()}.svg`}
                          alt="delivery truck logo"
                          width={100}
                          height={100}
                        />
                      </div>
                    </div>

                    <span className="font-[Roboto] font-bold text-[#353535]">
                      {item?.attributes?.name?.split("_")?.join(" ")}
                    </span>
                  </div>
                  <div>
                    <div className={`${reverseCols} flex gap-2 ltr:pr-4 rtl:pl-4 `}>
                      {item?.attributes?.reviewRate && (
                        <div className={`flex items-center pl-1`}>
                          <span>
                            <svg
                              width="15"
                              height="13"
                              viewBox="0 0 15 13"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M7.35219 0L9.00286 4.68945H14.3445L10.023 7.58769L11.6737 12.2771L7.35219 9.3789L3.03068 12.2771L4.68135 7.58769L0.359842 4.68945H5.70152L7.35219 0Z"
                                fill="#353535"
                              />
                            </svg>
                          </span>
                          <span>{rateFormatter(item?.attributes?.reviewRate)}</span>
                        </div>
                      )}
                      <div className={`flex items-center `}>
                        <span className="rtl:scale-x-[-1] pl-1">
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M10.6761 6.07456C10.8988 5.78029 11.022 5.41975 11.022 5.04463C11.022 4.44948 10.6893 3.88613 10.1538 3.57198C10.016 3.49112 9.85899 3.44856 9.69916 3.44871H6.52058L6.60011 1.81965C6.61867 1.42598 6.47949 1.05218 6.20908 0.767195C6.07638 0.626727 5.91628 0.514966 5.73868 0.438819C5.56108 0.362671 5.36974 0.323754 5.17651 0.324473C4.48724 0.324473 3.8775 0.788403 3.69458 1.45249L2.55596 5.57484H2.55199V11.248H8.81239C8.93434 11.248 9.05364 11.2242 9.16365 11.1765C9.7946 10.9074 10.2015 10.291 10.2015 9.60706C10.2015 9.44004 10.1777 9.27568 10.13 9.11661C10.3526 8.82235 10.4759 8.46181 10.4759 8.08669C10.4759 7.91967 10.4521 7.75531 10.4043 7.59625C10.627 7.30198 10.7503 6.94144 10.7503 6.56632C10.7476 6.39931 10.7238 6.23362 10.6761 6.07456V6.07456ZM0.417908 5.999V10.8239C0.417908 11.0585 0.607456 11.248 0.842072 11.248H1.70366V5.57484H0.842072C0.607456 5.57484 0.417908 5.76439 0.417908 5.999Z"
                              fill="#353535"
                            />
                          </svg>
                        </span>
                        <span>{likesFormatter(item?.attributes?.likesNbr || 0)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-1 md:col-span-2 flex items-center justify-center h-full bg-white rounded-xl ">
              <Image
                src={TbalerMoodEmpty}
                alt={"tabler mood empty icon"}
                width={30}
                height={30}
                className="w-[60px] lg:w-[90px] aspect-square"
              />
              <p className="font-[Roboto] font-bold text-[#BABABA]">{translate("No_stores")}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TopStores;
